'use client';

import { c as _c } from "react/compiler-runtime";
import { AppRouterInstance, NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import {
// eslint-disable-next-line no-restricted-imports
useRouter } from 'next/navigation';
import { TransitionStartFunction, createContext, useCallback, useContext, useMemo, useTransition } from 'react';
import { getIsNewWindowTargeted } from './link/functions/getIsNewWindowTargeted';
import { getHrefKind } from './link/functions/getHrefKind';
import { pushLinkEvent } from '../analytics/events/pushLinkEvent';
// eslint-disable-next-line no-restricted-imports
import { LinkProps } from 'next/link';
import { triggerDOPRouterEvent, useTriggerDOPRouterEvent } from './useDOPRouterEvent';
export type DOPRouter = AppRouterInstance & {
  onLinkClick: (event: React.MouseEvent<HTMLAnchorElement>, options?: {
    scroll?: LinkProps['scroll'];
    replace?: LinkProps['replace'];
  }) => void;
  startTransition: TransitionStartFunction;
  pushClient: (href: string) => void;
  replaceClient: (href: string) => void;
};
const DOPRouterContext = createContext<DOPRouter | undefined>(undefined);
const IsNavigatingContext = createContext<boolean | undefined>(undefined);
const getHashTarget = (hash: string) => {
  if (!hash) return document.body;
  const target = document.getElementById(hash.slice(1)) ?? document.body;
  return target;
};
const scrollToTarget = (target: HTMLElement) => {
  target.focus({
    preventScroll: true
  });
  if (target === document.body) {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  } else {
    target.scrollIntoView({
      behavior: 'smooth'
    });
  }
};
const getInternalSelfTarget = (href: string, options: NavigateOptions | undefined) => {
  const hrefKind = getHrefKind(href);
  if (!(options?.scroll ?? true)) return undefined;
  if (hrefKind !== 'INTERNAL_SELF') return undefined;
  const url = new URL(href, window.location.href);
  const target = getHashTarget(url.hash);
  return target;
};
export const DOPRouterProvider: React.FC<{
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(28);
  const {
    children
  } = t0;
  const [isNavigating, startTransition] = useTransition();
  const nextRouter = useRouter();
  useTriggerDOPRouterEvent();
  let t1;
  if ($[0] !== nextRouter) {
    t1 = (href, options) => {
      startTransition(() => {
        const internalSelfTarget = getInternalSelfTarget(href, options);
        if (internalSelfTarget) {
          if (options?.scroll ?? true) {
            scrollToTarget(internalSelfTarget);
          }
          nextRouter.push(href, {
            ...options,
            scroll: false
          });
          return;
        }
        nextRouter.push(href, options);
      });
    };
    $[0] = nextRouter;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const routerPush = t1;
  let t2;
  if ($[2] !== nextRouter) {
    t2 = (href_0, options_0) => {
      startTransition(() => {
        const internalSelfTarget_0 = getInternalSelfTarget(href_0, options_0);
        if (internalSelfTarget_0) {
          if (options_0?.scroll ?? true) {
            scrollToTarget(internalSelfTarget_0);
          }
          nextRouter.replace(href_0, {
            ...options_0,
            scroll: false
          });
          return;
        }
        nextRouter.replace(href_0, options_0);
      });
    };
    $[2] = nextRouter;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const routerReplace = t2;
  let t3;
  if ($[4] !== routerPush || $[5] !== routerReplace) {
    t3 = (event, t4) => {
      const {
        scroll: t5,
        replace: t6
      } = t4 === undefined ? {} : t4;
      const scroll = t5 === undefined ? true : t5;
      const replace = t6 === undefined ? false : t6;
      const link = event.currentTarget;
      const {
        href: href_1
      } = link;
      if (href_1 === "") {
        return;
      }
      const isNewWindowTargeted = getIsNewWindowTargeted(event);
      const hrefKind = getHrefKind(href_1);
      if (!isNewWindowTargeted) {
        if (hrefKind === "INTERNAL_SELF") {
          triggerDOPRouterEvent({
            destination: "self"
          });
        }
        if (hrefKind === "INTERNAL" || hrefKind === "INTERNAL_SELF") {
          event.preventDefault();
          if (replace) {
            routerReplace(href_1, {
              scroll
            });
          } else {
            routerPush(href_1, {
              scroll
            });
          }
        }
      }
      const dataset = {
        ...link.dataset
      };
      const linkType = link.getAttribute("data-link-type") || "";
      const text = link.innerText;
      pushLinkEvent({
        href: href_1,
        dataset,
        linkType,
        hrefKind,
        text
      });
    };
    $[4] = routerPush;
    $[5] = routerReplace;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = href_2 => {
      startTransition(() => {
        window.history.pushState(null, "", href_2);
      });
    };
    t5 = href_3 => {
      startTransition(() => {
        window.history.replaceState(null, "", href_3);
      });
    };
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  let t7;
  let t8;
  if ($[9] !== nextRouter) {
    t6 = () => {
      startTransition(() => {
        nextRouter.refresh();
      });
    };
    t7 = () => {
      startTransition(() => {
        nextRouter.back();
      });
    };
    t8 = () => {
      startTransition(() => {
        nextRouter.forward();
      });
    };
    $[9] = nextRouter;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
  }
  let t9;
  if ($[13] !== nextRouter.bfcacheId || $[14] !== nextRouter.prefetch || $[15] !== routerPush || $[16] !== routerReplace || $[17] !== t3 || $[18] !== t6 || $[19] !== t7 || $[20] !== t8) {
    t9 = {
      onLinkClick: t3,
      push: routerPush,
      replace: routerReplace,
      startTransition,
      pushClient: t4,
      replaceClient: t5,
      refresh: t6,
      back: t7,
      forward: t8,
      prefetch: nextRouter.prefetch,
      bfcacheId: nextRouter.bfcacheId
    };
    $[13] = nextRouter.bfcacheId;
    $[14] = nextRouter.prefetch;
    $[15] = routerPush;
    $[16] = routerReplace;
    $[17] = t3;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const router = t9;
  let t10;
  if ($[22] !== children || $[23] !== isNavigating) {
    t10 = <IsNavigatingContext.Provider value={isNavigating}>{children}</IsNavigatingContext.Provider>;
    $[22] = children;
    $[23] = isNavigating;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== router || $[26] !== t10) {
    t11 = <DOPRouterContext.Provider value={router}>{t10}</DOPRouterContext.Provider>;
    $[25] = router;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};
export const useDOPRouter = () => {
  const context = useContext(DOPRouterContext);
  if (context === undefined) {
    throw new Error("useDOPRouter must be used within a DOPRouterProvider");
  }
  return context;
};
export const useIsNavigating = () => {
  const context = useContext(IsNavigatingContext);
  if (context === undefined) {
    throw new Error("useIsNavigating must be used within a DOPRouterProvider");
  }
  return context;
};